export const USER_ROLES = {
  DEV_TEAM: 'DEV_TEAM',
  SCHOOL_ADMIN: 'SCHOOL_ADMIN',
  TEACHER: 'TEACHER',
  STUDENT: 'STUDENT',
  PARENT: 'PARENT',
} as const;

export const SCHOOL_STATUS = {
  ACTIVE: 'active',
  INACTIVE: 'inactive',
  SUSPENDED: 'suspended',
} as const;

export const ONBOARDING_STATUS = {
  PENDING: 'pending',
  IN_PROGRESS: 'in_progress',
  COMPLETED: 'completed',
} as const;

export const TOKEN_STORAGE_KEYS = {
  ACCESS_TOKEN: 'access_token',
  REFRESH_TOKEN: 'refresh_token',
} as const;

export const ROUTES = {
  // Landing & marketing
  LANDING: '/',
  MODULES: '/modules',
  TEACHER_LANDING: '/teacher',
  PRIVACY: '/privacy',
  /** Public MVP: Grade 3 workbook JSON renderer (no API). */
  GRADE3_ASSIGNMENTS_DEMO: '/demo/grade3-assignments',

  // Auth
  LOGIN: '/login',
  REGISTER: '/register',
  FORGOT_PASSWORD: '/forgot-password',
  RESET_PASSWORD: '/reset-password',
  INVITE_VERIFY: '/invite/verify',
  INVITE_ACCEPT: '/invite/accept',
  /** BanyanPro LMS launch bootstrap (public, legacy query-param flow). */
  LMS_LAUNCH: '/lms/launch',
  /** Production LMS authenticate (POST body, no JWT in URL). */
  AUTHENTICATE: '/authenticate',
  /** BanyanPro iframe embed shell (postMessage + optional query fallback). */
  EMBED_BANYANPRO: '/embed/banyanpro',

  // Profile (header dropdown; Coupons for DEV_TEAM)
  PROFILE: '/profile',
  PROFILE_COUPONS: '/profile/coupons',

  // Dev Team
  DEV_DASHBOARD: '/dev/dashboard',
  DEV_SCHOOLS: '/dev/schools',
  DEV_SCHOOLS_NEW: '/dev/schools/new',
  DEV_SCHOOL_DETAILS: '/dev/schools/:id',
  DEV_AUDIT_LOGS: '/dev/audit-logs',
  DEV_PLANS: '/dev/plans',
  DEV_INVITE: '/dev/invite',
  DEV_PROMPT_TEMPLATES: '/dev/prompt-templates',
  
  // School Admin
  ADMIN_DASHBOARD: '/admin/dashboard',
  ADMIN_SCHOOL: '/admin/school',
  ADMIN_SETUP: '/admin/setup',
  ADMIN_CLASSES: '/admin/classes',
  ADMIN_STUDENTS: '/admin/students',
  ADMIN_STUDENTS_NEW: '/admin/students/new',
  ADMIN_TEACHERS: '/admin/teachers',
  ADMIN_PARENTS: '/admin/parents',
  ADMIN_INVITE_TEACHER: '/admin/invites/teacher',
  ADMIN_INVITE_PARENT: '/admin/invites/parent',
  ADMIN_INVITE_STUDENT: '/admin/invites/student',
  ADMIN_PARENT_LINKS: '/admin/parent-links',
  ADMIN_SECTIONS: '/admin/sections',
  ADMIN_BILLING: '/admin/billing',

  // Teacher
  TEACHER_DASHBOARD: '/teacher/dashboard',
  TEACHER_PROFILE: '/teacher/profile',
  TEACHER_ASSIGNMENTS: '/teacher/assignments',
  /** Opens teacher Assignments → Communication (practice section scrolls into view). */
  TEACHER_ASSIGNMENTS_COMM_TASKS: '/teacher/assignments?open=communication',
  /** Teacher submission review (`:submissionId` = `submission_id`). */
  TEACHER_SUBMISSION_REVIEW: '/teacher/submissions/:submissionId/review',
  TEACHER_ANALYTICS: '/teacher/analytics',
  TEACHER_TUTOR_ANALYTICS: '/teacher/tutor-analytics',
  TEACHER_STUDENT_ANALYTICS: '/teacher/tutor-analytics/student/:studentId',
  TEACHER_CAN_DO: '/teacher/can-do',
  /** Legacy URL; AppRoutes redirects to TEACHER_ASSIGNMENTS_COMM_TASKS. */
  TEACHER_COMMUNICATION_ASSIGNMENTS: '/teacher/communication-assignments',
  TEACHER_COMMUNICATION_ANALYTICS: '/teacher/communication-analytics',
  TEACHER_COMMUNICATION_ALERTS: '/teacher/communication-alerts',
  TEACHER_SCENARIO_CAN_DO: '/teacher/scenario-can-do',
  TEACHER_STUDENT_COMMUNICATION_PROGRESS: '/teacher/student-progress',
  TEACHER_SETTINGS: '/teacher/settings',
  TEACHER_ONBOARDING: '/teacher/onboarding',
  
  // Student
  STUDENT_DASHBOARD: '/student/dashboard',
  STUDENT_PROFILE: '/student/profile',
  /** Grade 3 workbook (authenticated); same page component as public demo. */
  STUDENT_ACTIVITY: '/student/activity',
  STUDENT_CONVERSATION: '/student/conversation',
  STUDENT_TAMIL_TUTOR: '/student/ai-tutor-live',
  STUDENT_COMMUNICATION_TUTOR: '/student/communication-tutor',
  /** Deep link for communication practice tab on the unified assignments page. */
  STUDENT_ASSIGNMENTS_COMMUNICATION_TAB: '/student/assignments?tab=communication',
  /** Legacy path; AppRoutes redirects to STUDENT_ASSIGNMENTS_COMMUNICATION_TAB. */
  STUDENT_COMMUNICATION_ASSIGNMENTS: '/student/communication-assignments',
  STUDENT_SCENARIO_CONVERSATION: '/student/scenario-conversation',
  STUDENT_SCENARIO_CLASSROOM: '/student/scenario-conversation/classroom',
  STUDENT_GAMES: '/student/games',
  STUDENT_PHONICS_LAB: '/student/phonics-lab',
  STUDENT_ASSIGNMENTS: '/student/assignments',
  STUDENT_STORY_STUDIO: '/student/story-studio',
  STUDENT_AI_TUTOR_AVATAR: '/student/ai-tutor-avatar',
  STUDENT_PICTURE_TALK: '/student/picture-talk',
  // Full app path including :sceneId — prefer `studentPictureTalkScenePath(id)` for navigation.
  STUDENT_PICTURE_TALK_SCENE: '/student/picture-talk/:sceneId',
  STUDENT_WRITING: '/student/writing',
  STUDENT_WRITING_HISTORY: '/student/writing/history',
  STUDENT_COURSES: '/student/courses',
  STUDENT_COURSE_PLAYER: '/student/courses/:courseId',
  STUDENT_LIBRARY: '/student/library',
  STUDENT_PROGRESS: '/student/progress',
  STUDENT_ANALYTICS: '/student/analytics',
  STUDENT_ANALYTICS_CHARTS: '/student/analytics/charts',
  STUDENT_TUTOR_ANALYTICS: '/student/tutor-analytics',
  STUDENT_SETTINGS: '/student/settings',
  STUDENT_ONBOARDING: '/student/onboarding',
  
  // Parent
  PARENT_DASHBOARD: '/parent/dashboard',
  PARENT_LINK_CHILD: '/parent/link-child',
  PARENT_CHILDREN: '/parent/children',
  PARENT_COMMUNICATION_PROGRESS: '/parent/progress',
  TEACHER_MY_SECTIONS: '/teacher/my-sections',
  
  // Onboarding
  ONBOARDING_SCHOOL_SETUP: '/onboarding/school-setup',
  ONBOARDING_TEACHER_PROFILE: '/onboarding/teacher-profile',
  ONBOARDING_STUDENT_PROFILE: '/onboarding/student-profile',
  
  // Comms Admin
  ADMIN_COMMS_EMAIL_CONFIGS: '/admin/comms/email-configs',
  ADMIN_COMMS_EMAIL_TEMPLATES: '/admin/comms/email-templates',
  // Dev comms (same pages exposed under /dev)
  DEV_COMMS_EMAIL_CONFIGS: '/dev/comms/email-configs',
  DEV_COMMS_EMAIL_TEMPLATES: '/dev/comms/email-templates',
} as const;

/** Student Picture Talk detail URL (`/student/picture-talk/:sceneId`). */
export function studentPictureTalkScenePath(sceneId: string): string {
  return `${ROUTES.STUDENT_PICTURE_TALK}/${encodeURIComponent(sceneId)}`;
}

/** Teacher submission review URL (`/teacher/submissions/:submissionId/review`). */
export function teacherSubmissionReviewPath(submissionId: string): string {
  return `/teacher/submissions/${encodeURIComponent(submissionId)}/review`;
}

